import React, { useEffect, useState } from 'react';
import { Skeleton } from '@telus-uds/components-web';
import { createRACMClient, RACM_REMOTE_PR_URL } from '@telus/racm-sdk-browser';
import useAuth from '../hooks/useAuth';
import { userFromJwt } from '../lib/userFromJwt';
import apiMarketplace from './components/api-marketplace';
import apiMarketplaceStagingForProd from './components/api-marketplace-staging-for-prod';
import { generateMfePage } from './generateMfePage';

const RACM_CLIENTS: Record<string, { clientId: string; racmEnv: string }> = {
  localhost: { clientId: '7ebda3ab-3276-46cf-aa9d-c29520ca022a', racmEnv: 'env-localdev' },
  'simplify.developers.telus.digital': {
    clientId: 'faa8a4fd-197d-4e27-affd-5eea5d0f0991',
    racmEnv: 'env-development',
  },
  'simplify.staging.developers.telus.digital': {
    clientId: 'e3a0ad82-ce5e-473f-bec4-b0594066ea6a',
    racmEnv: 'env-staging',
  },
  'simplify.telus.com': {
    clientId: 'eb3c460e-128a-481c-922c-f4235996c3db',
    racmEnv: 'env-production',
  },
};

export const STAGING_FOR_PROD_FLAG = 'feature-flag-staging-for-prod';

let racmClient: ReturnType<typeof createRACMClient> | undefined;
const getRacmClient = () => {
  const { clientId, racmEnv } = RACM_CLIENTS[window.location.hostname] ?? RACM_CLIENTS.localhost;
  racmClient ??= createRACMClient({
    clientId,
    url: RACM_REMOTE_PR_URL,
    project: 'api-marketplace-mfe',
    appName: 'simplify',
    racmEnv,
  });
  return racmClient;
};

/** Whether RACM targets this user at the staging build in production. False if RACM is unreachable. */
export const isStagingForProdEnabled = async (userEmail: string): Promise<boolean> => {
  try {
    const client = getRacmClient();
    await client.start();
    await client.updateContext({ userEmail });
    return client.isEnabled(STAGING_FOR_PROD_FLAG);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Failed to initialize RACM client:', error);
    return false;
  }
};

const ApiMarketplaceMfe = generateMfePage(apiMarketplace.name, { config: apiMarketplace });
const ApiMarketplaceStagingForProdMfe = generateMfePage(apiMarketplaceStagingForProd.name, {
  config: apiMarketplaceStagingForProd,
});

/**
 * API Marketplace, optionally served from its staging build in production so a
 * subset of power users can validate a release before everyone gets it.
 */
export const ApiMarketplacePage = () => {
  const { jwt, isLoading } = useAuth();
  const email = userFromJwt(jwt)?.email || undefined;
  const [useStagingForProd, setUseStagingForProd] = useState<boolean>();

  useEffect(() => {
    if (isLoading) return undefined;
    if (!email) {
      setUseStagingForProd(false);
      return undefined;
    }
    let cancelled = false;
    void isStagingForProdEnabled(email).then(enabled => {
      if (!cancelled) setUseStagingForProd(enabled);
    });
    return () => {
      cancelled = true;
    };
  }, [isLoading, email]);

  if (useStagingForProd === undefined) return <Skeleton />;

  const Mfe = useStagingForProd ? ApiMarketplaceStagingForProdMfe : ApiMarketplaceMfe;
  return <Mfe userEmail={email} useStagingForProd={useStagingForProd} />;
};
